import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import ContactUs from './pages/ContactUs';
import clsx from 'clsx';
import { Sling as Hamburger } from 'hamburger-react';
import EventDetails from './pages/EventDetails';
import FAQs from './pages/FAQs';

// Create a component that has access to useLocation
function ModalContent({ isModalOpen, setIsModalOpen }: { isModalOpen: boolean; setIsModalOpen: (open: boolean) => void }) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isContactUs = location.pathname === '/contact-us';
  const isEventDetails = location.pathname === '/event-details';
  const isFAQs = location.pathname === '/faqs';

  return (
    <>
      {isModalOpen && (
        <div className="fixed md:hidden inset-0 bg-secondary-light z-50 flex flex-col p-12 pt-24">
          <ul className="w-full text-right">
            <li className="text-secondary py-2 bg-linen w-full border border-black/20">
              {isHome ? (
                <span className="text-lg pr-2 italic font-archer font-bold text-black cursor-not-allowed">Home</span>
              ) : (
                <Link to="/" onClick={() => setIsModalOpen(false)} className="hover:underline text-lg pr-2 italic font-archer font-bold">Home</Link>
              )}
            </li>
            <li className="text-secondary py-2 bg-linen w-full border border-black/20">
              {isEventDetails ? (
                <span className="text-lg pr-2 italic font-archer font-bold text-black cursor-not-allowed">Event Details</span>
              ) : (
                <Link to="/event-details" onClick={() => setIsModalOpen(false)} className="hover:underline text-lg pr-2 italic font-archer font-bold">Event Details</Link>
              )}
            </li>
            <li className="text-secondary py-2 bg-linen w-full border border-black/20">
              {isFAQs ? (
                <span className="text-lg pr-2 italic font-archer font-bold text-black cursor-not-allowed">FAQs</span>
              ) : (
                <Link to="/faqs" onClick={() => setIsModalOpen(false)} className="hover:underline text-lg pr-2 italic font-archer font-bold">FAQs</Link>
              )}
            </li>
            <li className="text-secondary py-2 bg-linen w-full border border-black/20">
              {isContactUs ? (
                <span className="text-lg pr-2 italic font-archer font-bold text-black cursor-not-allowed">Contact Us</span>
              ) : (
                <Link to="/contact-us" onClick={() => setIsModalOpen(false)} className="hover:underline text-lg pr-2 italic font-archer font-bold">Contact Us</Link>
              )}
            </li>
          </ul>
        </div>
      )}
    </>
  );
}

// Desktop menu component that has access to useLocation
export function DesktopMenu() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isContactUs = location.pathname === '/contact-us';
  const isEventDetails = location.pathname === '/event-details';
  const isFAQs = location.pathname === '/faqs';

  function getLinkClasses(isActive: boolean) {
    return clsx("hover:underline text-lg italic font-archer font-bold text-white cursor-pointer", isActive && "underline bg-secondary/80");
  }

  return (
    <div className="hidden md:block">
      <ul className="flex items-center gap-4 justify-center">
        <li className="px-8 py-4">
          <Link 
            to="/" 
            className={getLinkClasses(isHome)}
          >
            Home
          </Link>
        </li>
        <li className="px-8 py-4">
          <Link 
            to="/event-details" 
            className={getLinkClasses(isEventDetails)}
          >
            Event Details
          </Link>
        </li>
        <li className="px-8 py-4">
          <Link 
            to="/faqs" 
            className={getLinkClasses(isFAQs)}
          >
            FAQs
          </Link>
        </li>
        <li className="px-8 py-4">
          <Link 
            to="/contact-us" 
            className={getLinkClasses(isContactUs)}
          >
            Contact Us
          </Link>
        </li>
      </ul>
    </div>
  );
}

export default function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Router>
      <ScrollToTop />
      <div className="bg-primary pb-0 md:pb-12 min-h-screen">
        {/* Modal - Full Page */}
        <ModalContent isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />

        {/* Mobile menu - Available on all pages */}
        <div className="w-full relative flex md:hidden justify-center p-8">
          <div className="flex items-center">
            <img src="/heart.png" alt="Heart" className="w-12 aspect-square" />
            <img src="/heart.png" alt="Heart" className="w-12 aspect-square scale-x-[-1]" />
          </div>
          <div className="absolute top-8 right-8 bg-linen aspect-square z-[60]">
            <Hamburger toggled={isModalOpen} toggle={setIsModalOpen} color="#939A6E" size={24} />
          </div>
        </div>

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/event-details" element={<EventDetails />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/contact-us" element={<ContactUs />} />
        </Routes>
      </div>
    </Router>
  );
};

// Scroll to top of page when route changes
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, [pathname]);

  return null;
}
